import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import {
  Query,
  QueryCache,
  QueryClient,
  QueryClientProvider,
  QueryKey,
} from '@tanstack/react-query';

import { QueryParamProvider, QueryParamOptions } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { parse, stringify } from 'query-string';
import { AxiosError } from 'axios';

import '@app/index.css';
import '@app/i18n';
import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';
import App from '@app/App';

import { ThemeProvider } from '@themes/themeContext';
import { AuthProvider } from '@auth_module/context/AuthContext';
import { CustomerProvider } from '@account_module/context/CustomerContext';
import { refreshToken } from '@auth_module/services';
import { STORAGE_KEY } from '@auth_module/constants/storageKeys';
import { IRequestError } from '@configs/axios-instances';
import { AccountProvider } from '@account_module/context/AccountContext';

let isRefreshing = false;
let failedRequestQueue: Query<unknown, unknown, unknown, QueryKey>[] = [];

function isTokenExpired(token: string) {
  const decoded = jwtDecode(token) as { exp: number };
  return decoded.exp < Date.now();
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      refetchInterval: undefined,
    },
  },
  queryCache: new QueryCache({
    onError: async (error, query) => {
      const axiosError = error as AxiosError<IRequestError>;
      console.log('Query error:', error);

      // if error is to many requests, we will retry the query after 1 second
      if (axiosError?.status === 429) {
        setTimeout(() => {
          query.invalidate();
          query.fetch();
        }, 1000);
      }

      // if error is 401 and token is expired, we will add failed query to queue and try to refresh token
      else if (axiosError?.status === 401) {
        const token = Cookies.get(STORAGE_KEY.auth_token);

        if (token && isTokenExpired(token)) {
          failedRequestQueue.push(query);
        }

        if (token && !isRefreshing && isTokenExpired(token)) {
          isRefreshing = true;
          try {
            await refreshToken();
            isRefreshing = false;
          } catch (error) {
            const axiosError = error as AxiosError<IRequestError>;
            isRefreshing = false;
            if (isTokenExpired(token) && axiosError?.status === 404) {
              Cookies.remove(STORAGE_KEY.auth_token);
              window.location.reload();
            }
          }

          // after new token is set, we will retry all failed queries
          failedRequestQueue.forEach((query) => {
            query.invalidate();
            query.fetch();
          });
          failedRequestQueue = [];
        }
      }
    },
  }),
});

const queryParamOptions: QueryParamOptions = {
  // https://github.com/pbeshai/use-query-params/tree/master/packages/use-query-params#queryparamoptions
  searchStringToObject: parse,
  objectToSearchString: stringify,
  updateType: 'replaceIn',
};

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CustomerProvider>
          <BrowserRouter>
            <ThemeProvider>
              <QueryParamProvider adapter={ReactRouter6Adapter} options={queryParamOptions}>
                <AccountProvider>
                  <App />
                </AccountProvider>
              </QueryParamProvider>
            </ThemeProvider>
          </BrowserRouter>
        </CustomerProvider>
      </AuthProvider>
      {/* <ReactQueryDevtools initialIsOpen={true} /> */}
    </QueryClientProvider>
  </React.StrictMode>
);
